import { AxiosProgressEvent, AxiosPromise } from "axios";
import { Sort } from "../../components/@types";
import { route } from "../../shim/jsbridge";
import { Timerange } from "../search/@types";
import {
  GetYaraRevisionDetailsResponse,
  GetYaraRevisionHistoryForRulesetResponse,
  GetYaraRulesForRevisionResponse,
  GetYaraSamplesRefData,
  IUploadYaraResponse,
  IUploadYaraRulesetRequest,
  IYaraActionResponse,
  IYaraBulkPublishResponse,
  IYaraDeleteParams,
  IYaraLocalRetroHuntResponse,
  IYaraRefDataResponse,
  IYaraRepoDataItem,
  IYaraRepoFormData,
  IYaraRepoListResponse,
  IYaraRulesetsResponse,
  IYaraStatusResponse,
  IYaraSyncStatus,
  RemoteRetroHuntAction,
  RemoteRetroHuntActionResponse,
  RemoteRetroHuntPayload,
  RetroHunt,
  RetroHuntBucketsResponse,
  YaraCloudStatusResponse,
  YaraConnectionStatusResponse,
  YaraMatchesResponse,
  YaraRequest,
  YaraTestRunData,
  YaraTestRunRequest,
  YaraTestRunResponse,
  YaraTestRunResultsResponse,
  YaraTestRunSamplesResponse,
  YaraUnsubscribeResponse
} from "./@types";
import { getFilterParams } from "./dashboard/utils";
import { AxiosApiWrapper } from "../../services/api/AxiosApiWrapper";

const { csrfToken } = window.tcbase;

class Api extends AxiosApiWrapper {
  postYaraFile = (
    request: { file: File },
    options: {
      onUploadProgress: (event: AxiosProgressEvent) => void;
      signal?: AbortSignal;
    }
  ) => {
    if (!csrfToken) {
      throw new Error("CSRFToken function is missing. Please fix it!");
    }
    const body = new FormData();
    body.append("file", request.file);
    body.append("csrfmiddlewaretoken", csrfToken());

    return this.post(route("yara_parse"), body, options);
  };

  checkYaraValidity = ({ content, name, enabled, ticloud }: YaraRequest) => {
    const formData = new FormData();
    formData.append("content", content);
    formData.append("name", name);
    formData.append("enabled", String(enabled));
    formData.append("ticloud", String(ticloud));

    return this.post(route("yara_validation"), formData);
  };

  createFromUploadYara = (data: IUploadYaraRulesetRequest) => {
    const bodyFormData = new FormData();
    try {
      const items = JSON.stringify(data.items);
      bodyFormData.append("items", items);
      bodyFormData.append("uploadSessionId", data.uploadSessionId);
      return this.post(route("yara_create_from_upload"), bodyFormData);
    } catch (e) {
      console.error(e);
      throw new Error("Invalid JSON!");
    }
  };

  getYaraRepositoriesList = (
    activeFilter?: string
  ): AxiosPromise<IYaraRepoListResponse> =>
    this.get(route("api_yara_repositories_list_create"), {
      params: {
        active_filter: activeFilter
      }
    });

  createOrUpdateYaraRepo = (data: IYaraRepoFormData) => {
    if (data.id != null) {
      return this.put(
        route("api_yara_repositories_update_delete", {
          repository_id: data.id
        }),
        data
      );
    } else {
      return this.post(route("api_yara_repositories_list_create"), data);
    }
  };

  deleteYaraRepo = ({ repoId, shouldRemoveRulesets }: IYaraDeleteParams) =>
    this.delete(
      route("api_yara_repositories_update_delete", {
        repository_id: repoId
      }),
      {
        params: {
          shouldRemoveRulesets
        }
      }
    );

  getYaraOnlineParsedRulesets = (
    source: IYaraRepoDataItem | undefined
  ): AxiosPromise<Array<IUploadYaraResponse>> => {
    const uploadSessionId = Math.random().toString(36).substring(2, 9);
    const bodyFormData = new FormData();
    if (source) {
      Object.entries(source)
        .filter(([, value]) => value != null)
        .forEach(([key, value]) => {
          bodyFormData.append(key, value);
        });
    }
    bodyFormData.append("uploadSessionId", uploadSessionId);
    return this.post(route("yara_repositories_parse"), bodyFormData);
  };

  checkYaraRepositoryValidity = (data: IYaraRepoFormData) =>
    this.post(route("yara_repository_validate"), data);

  getYaraStatus = (): AxiosPromise<IYaraStatusResponse> =>
    this.get(route("yara_status"));

  getYaraCloudStatus = (): AxiosPromise<YaraCloudStatusResponse> =>
    this.get(route("yara_status_cloud"));

  toggleLocalRetroHunt = (
    action: "STOP" | "START"
  ): AxiosPromise<IYaraLocalRetroHuntResponse> =>
    this.post(route("yara_local_retro_hunt"), { action });

  updateCmRetroHuntStatus = (
    action: RemoteRetroHuntAction,
    id: string
  ): AxiosPromise<RemoteRetroHuntActionResponse> =>
    this.post(route("api-post-retro-hunt-action", { uuid: id }), { action });

  startCmRetroHunt = (hubGroup: string, payload: RemoteRetroHuntPayload) =>
    this.post(route("api-post-retro-hunt", { hub_group: hubGroup }), payload);

  getYaraRulesets = (
    nameFilter?: string,
    ownerFilter?: string,
    statusFilter?: string,
    sourceFilter?: string,
    sort?: Sort,
    includeNewMatches?: boolean
  ): AxiosPromise<IYaraRulesetsResponse> =>
    this.get(route("yara_data"), {
      params: {
        name_filter: nameFilter,
        owner_filter: ownerFilter,
        status_filter: statusFilter,
        source_filter: sourceFilter,
        order_by: sort?.field,
        sort_direction: sort?.direction,
        include_new_matches: includeNewMatches
      }
    });

  getYaraRefData = (): AxiosPromise<IYaraRefDataResponse> =>
    this.get(route("yara_ref_data"));

  getAllTags = (): AxiosPromise<Array<string>> =>
    this.get(route("tags_all_user_tags"));

  getRulesetTestResults = (qp?: {
    revision_id: string;
  }): AxiosPromise<Array<YaraTestRunResultsResponse>> =>
    this.get(route("yara_get_test_ruleset", {}, qp || {}));

  getRulesetTestResult = (
    id: string | undefined
  ): AxiosPromise<YaraTestRunResultsResponse> =>
    this.get(route("yara_get_test_ruleset", [id]));

  postRulesetTest = (
    request: YaraTestRunRequest
  ): AxiosPromise<YaraTestRunResponse> =>
    this.post(route("yara_test_ruleset"), request);

  postRulesetReanalyze = (id: string | null) =>
    this.post(route("yara_ruleset_reanalyze", [id]));

  getYaraTestRunSamples = (
    request: YaraTestRunData
  ): AxiosPromise<YaraTestRunSamplesResponse> =>
    this.post(route("yara_testrun_samples"), request);

  unsubscribeToYaraRulesets = (
    rulesetNames: string[] | null
  ): AxiosPromise<YaraUnsubscribeResponse> =>
    this.post(route("api-unsubscribe-yara-rulesets"), {
      names: rulesetNames
    });

  toggleFavorite = (
    rulesetName: string,
    isFavorite: boolean
  ): AxiosPromise<YaraUnsubscribeResponse> =>
    this.post(
      route("api-yara-ruleset-toggle-favorite", {
        ruleset_name: rulesetName
      }),
      { state: isFavorite }
    );

  doActionOnRuleset = (
    rulesetId: number | null,
    actionName: string,
    publish: boolean
  ): AxiosPromise<IYaraActionResponse> =>
    this.get(
      route(
        "yara_action",
        {},
        {
          id: rulesetId,
          action: actionName,
          publish
        }
      )
    );

  fetchAndAnalyzeAllSamples = (
    rulesetId: number,
    ruleId?: string | null,
    classificationIds?: string,
    fileFormatName?: string | null,
    threatTypeName?: string | null,
    timerange?: Timerange
  ) =>
    this.get(route("api-yara-fetch-and-analyze-all"), {
      params: {
        revision_id: rulesetId,
        rule_id: ruleId,
        timerange_start: timerange?.start,
        timerange_end: timerange?.end,
        threat_type_name: threatTypeName,
        classification_ids: classificationIds,
        file_format_name: fileFormatName
      }
    });

  removeAllCloudMatches = (
    rulesetId: number,
    ruleId?: string | null,
    classificationName?: string | null,
    fileFormatName?: string | null,
    threatTypeName?: string | null,
    timerange?: Timerange
  ) =>
    this.get(
      route(
        "yara_remove_all_matches",
        {},
        getFilterParams(
          rulesetId,
          ruleId,
          classificationName,
          fileFormatName,
          threatTypeName,
          timerange
        )
      )
    );

  removeCloudMatchesBySampleRuleIds = (
    rulesetId: number,
    sampleRuleIds: number[]
  ) =>
    this.get(
      route(
        "yara_remove_matches",
        {},
        {
          revision_id: rulesetId,
          id: JSON.stringify(sampleRuleIds)
        }
      )
    );

  getYaraRevisionHistoryForRuleset = (
    rulesetId: number
  ): AxiosPromise<GetYaraRevisionHistoryForRulesetResponse> =>
    this.get(
      route(
        "yara_history",
        {},
        {
          id: rulesetId
        }
      )
    );

  getYaraRulesForRevision = (
    revisionId: number
  ): AxiosPromise<GetYaraRulesForRevisionResponse> =>
    this.get(
      route(
        "yara_rules",
        {},
        {
          id: revisionId
        }
      )
    );

  getYaraRevisionDetails = (
    revisionId?: number | string
  ): AxiosPromise<GetYaraRevisionDetailsResponse> =>
    this.get(
      route(
        "yara_details",
        {},
        {
          id: revisionId
        }
      )
    );

  getYaraMatches = (
    revisionId: number,
    pageSize: number,
    pageNum: number,
    ruleId?: string | null,
    timerange?: Timerange,
    threatTypeName?: string | null,
    classificationIds?: string,
    fileFormatName?: string | null,
    matchesType?: string | null,
    sortField?: string,
    sortDir?: string
  ): AxiosPromise<YaraMatchesResponse> =>
    this.get(route("api-yara-fetch-uploads"), {
      params: {
        revision_id: revisionId,
        rule_id: ruleId,
        timerange_start: timerange?.start,
        timerange_end: timerange?.end,
        threat_type_name: threatTypeName,
        classification_ids: classificationIds,
        file_format_name: fileFormatName,
        source_choice: matchesType,
        page_size: pageSize,
        page_num: pageNum,
        sort_field: sortField,
        sort_dir: sortDir
      }
    });

  getYaraRevisionAggregated = (
    revisionId?: number,
    rulesetId?: number,
    ruleId?: string | null,
    timerange?: Timerange,
    threatTypeName?: string | null,
    classificationIds?: string,
    fileFormatName?: string | null,
    matchesType?: string | null
  ): AxiosPromise<GetYaraSamplesRefData> =>
    this.get(route("api-yara-uploads-aggregated"), {
      params: {
        revision_id: revisionId,
        ruleset_id: rulesetId,
        rule_id: ruleId,
        timerange_start: timerange?.start,
        timerange_end: timerange?.end,
        threat_type_name: threatTypeName,
        classification_ids: classificationIds,
        file_format_name: fileFormatName,
        source_choice: matchesType
      }
    });

  getYaraSyncStatus = (): AxiosPromise<IYaraSyncStatus> =>
    this.get(route("api_yara_cm_sync_status"));

  bulkPublishYaraRuleset = (
    revisionIds: number[]
  ): AxiosPromise<IYaraBulkPublishResponse> =>
    this.post(route("api-yara-ruleset-bulk-publish"), {
      revision_ids: revisionIds
    });

  getYaraRetroHuntS3Buckets = (
    signal?: AbortSignal
  ): AxiosPromise<RetroHuntBucketsResponse> =>
    this.get(route("api-get-retro-hunt-s3-buckets"), signal && { signal });

  getYaraConnectionStatus = (): AxiosPromise<YaraConnectionStatusResponse> =>
    this.get(route("yara-connection-status"));

  getRemoteRetroHuntList = (signal?: AbortSignal): AxiosPromise<RetroHunt> =>
    this.get(route("api-s3-retro-hunt"), signal && { signal });
}

export default new Api();
